import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEmployeesResourceBlocks } from '../../services/employee';
import { isSameDayOrAfter, isSameDayOrBefore } from '../../helpers/global';

interface Params {
  employeeIds: string[];
  dataStartDate?: Date;
  dataEndDate?: Date;
}

function useGetBatchTimeBlocks({
  employeeIds,
  dataStartDate,
  dataEndDate,
}: Params) {
  const sortedIds = [...employeeIds].sort();

  const { data, isLoading, isFetching, isError, isSuccess, error } = useQuery({
    queryKey: ['timeBlocksBatch', sortedIds],
    queryFn: async () => {
      if (!sortedIds.length) return {};
      const response = await getEmployeesResourceBlocks(sortedIds);
      return response ?? {};
    },
  });

  const filteredData = React.useMemo(() => {
    const result = Object.keys(data ?? {}).reduce<NonNullable<typeof data>>(
      (acc, key) => {
        if (sortedIds.includes(key)) {
          const value = data?.[key];
          if (value) {
            let tempTimeBlocks = value;
            if (dataStartDate) {
              tempTimeBlocks = tempTimeBlocks?.filter(
                timeBlock =>
                  isSameDayOrAfter(new Date(timeBlock.endDate), dataStartDate), // ends after start date
              );
            }
            if (dataEndDate) {
              tempTimeBlocks = tempTimeBlocks?.filter(timeBlock =>
                isSameDayOrBefore(new Date(timeBlock.startDate), dataEndDate),
              );
            }
            acc[key] = tempTimeBlocks;
          }
        }
        return acc;
      },
      {},
    );
    return result;
  }, [data, dataEndDate, dataStartDate, sortedIds]);

  return {
    data: filteredData,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
  };
}

export default useGetBatchTimeBlocks;
